import { FeatureCollection, Feature } from "geojson";
import { RxType, PlotType } from "@/types/enums";
import { useMemo, useEffect, useState } from "react";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Spinner,
} from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { InferType, object, number, array, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPencil,
    faTrash,
    faDrawPolygon,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import NumberInput from "@/components/inputs/NumberInput";
import useMap from "@/lib/map/useMap";
import useAuth from "@/lib/auth/useAuth";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerVisibility,
    moveLayersToTop,
} from "@/lib/utils/map";
import {
    PLOT_HIGHLIGHT_SOURCE,
    PLOT_HIGHLIGHT_LINE_LAYER,
} from "@/lib/map/layers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlags from "@/lib/featureFlags/useFeatureFlags";

const schema = object().shape({
    userIsSentinel: boolean().required(),
    plots: array().when("userIsSentinel", {
        is: (userIsSentinel: boolean) => userIsSentinel,
        then: (schema) =>
            schema.of(
                object().shape({
                    index: number().required(),
                    id: number().required(),
                    plot_type: number()
                        .oneOf([PlotType.Canary, PlotType.Reference])
                        .required(),
                    rate_offset: number()
                        .required()
                        .label("Rate Offset")
                        .when(["plot_type"], {
                            is: (plotType: PlotType) =>
                                plotType === PlotType.Canary,
                            then: (schema) => schema.max(0),
                            otherwise: (schema) => schema.min(0),
                        }),
                }),
            ),
        otherwise: (schema) =>
            schema.of(
                object().shape({
                    index: number().required(),
                    id: number().required(),
                    plot_type: number()
                        .oneOf([PlotType.Canary, PlotType.Reference])
                        .required(),
                    rate_offset: number()
                        .required()
                        .label("Rate Offset")
                        .when(["plot_type"], {
                            is: (plotType: PlotType) =>
                                plotType === PlotType.Canary,
                            then: (schema) => schema.max(-30),
                            otherwise: (schema) => schema.min(30),
                        }),
                }),
            ),
    }),
});

type FormType = InferType<typeof schema>;

function RxPlotTable({
    plots,
    rxType,
    onUpdate,
}: {
    plots: FeatureCollection;
    rxType: RxType;
    onUpdate?: () => void;
}) {
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: "onBlur",
    });

    const { t } = useTranslation();
    const { useMutateRequest } = useRequest();
    const { map, mapLoaded } = useMap();
    const { userIsSentinel } = useAuth();
    const { fullPlotOffsetEnabled } = useFeatureFlags();
    const [selectedPlot, setSelectedPlot] = useState<Feature | null>(null);
    const updatePlotRequest = useMutateRequest<Feature>({
        method: "patch",
        successMessage: t("success.updated_plot", { ns: "messages" }),
        errorMessage: t("error.error_updating_plot", { ns: "messages" }),
    });
    const deletePlotsRequest = useMutateRequest({
        method: "post",
        url: "/plots/delete/",
        successMessage: t("success.deleted_plots", { ns: "messages" }),
        errorMessage: t("error.error_deleting_plots", { ns: "messages" }),
    });
    const { formatUnit } = useI18n();
    const columns = useMemo(() => {
        switch (rxType) {
            case RxType.ZONE:
                return [
                    { key: "pair_id", label: t("pair_id", { ns: "rx" }) },
                    { key: "rate_offset", label: t("n_offset", { ns: "rx" }) },
                    { key: "n_rate", label: t("n_rate", { ns: "rx" }) },
                    {
                        key: "product_rate",
                        label: t("product_rate", { ns: "rx" }),
                    },
                    { key: "acres", label: t("acres", { ns: "rx" }) },
                    { key: "actions", label: t("actions", { ns: "rx" }) },
                ];
            case RxType.VRF:
                return [
                    { key: "pair_id", label: t("pair_id", { ns: "rx" }) },
                    { key: "rate_offset", label: t("n_offset", { ns: "rx" }) },
                    { key: "n_rate", label: t("n_rate", { ns: "rx" }) },
                    {
                        key: "product_rate",
                        label: t("product_rate", { ns: "rx" }),
                    },
                    {
                        key: "injection_rate",
                        label: t("injection_rate", { ns: "rx" }),
                    },
                    { key: "acres", label: t("acres", { ns: "rx" }) },
                    { key: "actions", label: t("actions", { ns: "rx" }) },
                ];
            case RxType.VRI:
                return [
                    { key: "pair_id", label: t("pair_id", { ns: "rx" }) },
                    { key: "rate_offset", label: t("n_offset", { ns: "rx" }) },
                    {
                        key: "target_n_rate",
                        label: `${t("target_n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`,
                    },
                    { key: "n_rate", label: t("actual_n", { ns: "rx" }) },
                    {
                        key: "product_rate",
                        label: t("product_rate", { ns: "rx" }),
                    },
                    { key: "depth", label: t("depth", { ns: "rx" }) },
                    { key: "speed", label: t("speed", { ns: "rx" }) },
                    { key: "acres", label: t("acres", { ns: "rx" }) },
                    { key: "actions", label: t("actions", { ns: "rx" }) },
                ];
            default:
                return [];
        }
    }, [formatUnit, rxType, t]);

    const sortedPlots = useMemo(() => {
        return plots.features.sort((a: Feature, b: Feature) => {
            return a.properties?.pair_id == b.properties?.pair_id
                ? a.properties?.plot_type - b.properties?.plot_type
                : a.properties?.pair_id - b.properties?.pair_id;
        });
    }, [plots.features]);
    useEffect(() => {
        form.reset({
            userIsSentinel: userIsSentinel || fullPlotOffsetEnabled,
            plots: sortedPlots.map((plot: Feature) => ({
                index: sortedPlots.indexOf(plot),
                id: Number(plot.id),
                plot_type: plot.properties?.plot_type,
                rate_offset: plot.properties?.rate_offset,
            })),
        });
    }, [sortedPlots, form, userIsSentinel, fullPlotOffsetEnabled]);
    useEffect(() => {
        if (!map || !mapLoaded) return;
        addUpdateGeojsonSource(map, PLOT_HIGHLIGHT_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: selectedPlot ? [selectedPlot] : [],
            },
        });
        addUpdateGeojsonLayers(map, [PLOT_HIGHLIGHT_LINE_LAYER]);
        updateLayerVisibility(map, [PLOT_HIGHLIGHT_LINE_LAYER.id], true);
        moveLayersToTop(map, [PLOT_HIGHLIGHT_LINE_LAYER.id]);

        return () => {
            if (map) {
                removeLayersIfExists(map, [PLOT_HIGHLIGHT_LINE_LAYER.id]);
                removeSourcesIfExists(map, [PLOT_HIGHLIGHT_SOURCE]);
            }
        };
    }, [map, mapLoaded, selectedPlot]);

    const handleCellUpdate = debounce((plot: Feature, plotIndex: number) => {
        if (!plot?.id) return;
        setSelectedPlot(plot);
        if (form.getFieldState(`plots.${plotIndex}.rate_offset`).error) return;
        const value = Number(form.getValues(`plots.${plotIndex}.rate_offset`));
        updatePlotRequest.mutate(
            {
                url: `/plots/${plot.id}/`,
                data: {
                    properties: {
                        rate_offset: value,
                    },
                },
            },
            {
                onSuccess: (data: Feature) => {
                    plots.features = plots.features.map((feature: Feature) => {
                        if (feature.id === data.id) {
                            return data;
                        }
                        return feature;
                    });
                    onUpdate?.();
                },
            },
        );
    }, 500);
    const deletePlots = (pairId: number | null) => {
        deletePlotsRequest.mutate(
            {
                data: plots.features
                    .filter(
                        (feature: Feature) =>
                            !pairId || feature.properties?.pair_id === pairId,
                    )
                    .map((feature: Feature) => {
                        return feature.id;
                    }),
            },
            {
                onSuccess: () => {
                    onUpdate?.();
                },
            },
        );
    };

    return plots.features.length === 0 ? (
        <div className="flex flex-row justify-between items-center p-3">
            <h2>{t("sentinel_plots", { ns: "rx" })}</h2>
            <div className="flex flex-row gap-2">
                <Button
                    size="sm"
                    color="secondary"
                    as={Link}
                    to="/dashboard/fields/$fieldId/rx/$rxId/plots/place/"
                >
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                    {t("auto_place_plots", { ns: "rx" })}
                </Button>
                <Button
                    size="sm"
                    color="secondary"
                    as={Link}
                    to="/dashboard/fields/$fieldId/rx/$rxId/plots/draw/"
                >
                    <FontAwesomeIcon icon={faDrawPolygon} />
                    {t("draw_plots", { ns: "rx" })}
                </Button>
            </div>
        </div>
    ) : (
        <FormProvider {...form}>
            <form>
                <Table
                    aria-label="Rx Plot Table"
                    removeWrapper={true}
                    isCompact={true}
                    radius="none"
                    topContent={
                        <div className="flex flex-row justify-between items-center p-1">
                            <h2>{t("sentinel_plots", { ns: "rx" })}</h2>
                            <div className="flex flex-row gap-2">
                                <Button
                                    size="sm"
                                    color="danger"
                                    onPress={() => deletePlots(null)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    {t("delete_all", { ns: "rx" })}
                                </Button>
                                <Button
                                    size="sm"
                                    color="secondary"
                                    as={Link}
                                    to="/dashboard/fields/$fieldId/rx/$rxId/plots/draw/"
                                >
                                    <FontAwesomeIcon icon={faDrawPolygon} />
                                    {t("add_plot_pair", { ns: "rx" })}
                                </Button>
                            </div>
                        </div>
                    }
                    classNames={{
                        base: "overflow-x-scroll",
                        thead: "[&>tr]:first:rounded-none [&>tr[aria-hidden]]:hidden border-1 border-gray-300",
                        th: "rounded-none first:rounded-none last:rounded-none",
                    }}
                >
                    <TableHeader columns={columns}>
                        {(column) => (
                            <TableColumn
                                key={column.key}
                                width="80"
                                align={
                                    column.key === "pair_id" ? "center" : "end"
                                }
                            >
                                {column.label}
                            </TableColumn>
                        )}
                    </TableHeader>
                    <TableBody
                        items={sortedPlots}
                        isLoading={
                            updatePlotRequest.isPending ||
                            deletePlotsRequest.isPending
                        }
                        loadingContent={<Spinner label="loading" />}
                    >
                        {(item) => (
                            <TableRow
                                key={item.id}
                                className="border-1 border-gray-300"
                                onMouseEnter={() => {
                                    setSelectedPlot(item);
                                }}
                                onMouseLeave={() => {
                                    setSelectedPlot(null);
                                }}
                            >
                                {(columnKey) => {
                                    const value = item.properties?.[columnKey];
                                    const plotType = item.properties?.plot_type;
                                    const className =
                                        plotType === 1
                                            ? "bg-danger-500 bg-opacity-50"
                                            : "bg-success-500 bg-opacity-50";
                                    if (columnKey === "pair_id") {
                                        return plotType === 1 ? (
                                            <TableCell
                                                key={columnKey}
                                                rowSpan={2}
                                            >
                                                {value}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                key={columnKey}
                                                style={{ display: "none" }}
                                            >
                                                {value}
                                            </TableCell>
                                        );
                                    }
                                    if (columnKey === "rate_offset") {
                                        const plotIndex =
                                            sortedPlots.indexOf(item);
                                        return (
                                            <TableCell
                                                key={columnKey}
                                                className={className}
                                            >
                                                <NumberInput
                                                    hideErrorMessage
                                                    aria-label="Rate Offset"
                                                    name={`plots.${plotIndex}.rate_offset`}
                                                    size="sm"
                                                    radius="sm"
                                                    onAutosave={() =>
                                                        handleCellUpdate(
                                                            item,
                                                            plotIndex,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        );
                                    }
                                    if (columnKey === "actions") {
                                        return plotType === 1 ? (
                                            <TableCell
                                                key={columnKey}
                                                rowSpan={2}
                                            >
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="bordered"
                                                    color="primary"
                                                    as={Link}
                                                    to={`/dashboard/fields/$fieldId/rx/$rxId/plots/${item.properties?.pair_id}/`}
                                                    className="mb-0.5"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPencil}
                                                    />
                                                </Button>
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="bordered"
                                                    color="danger"
                                                    onPress={() =>
                                                        deletePlots(
                                                            Number(
                                                                item?.properties
                                                                    ?.pair_id,
                                                            ),
                                                        )
                                                    }
                                                    className="mt-0.5"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                    />
                                                </Button>
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                key={columnKey}
                                                style={{ display: "none" }}
                                            >
                                                {value}
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell
                                            key={columnKey}
                                            className={className}
                                        >
                                            {value?.toFixed(2) || "-"}
                                        </TableCell>
                                    );
                                }}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </form>
        </FormProvider>
    );
}

export default RxPlotTable;
